import type { EnqueueAutomationMoveInput } from './useEnqueueAutomationMove';
import type {
  WorkflowExecutionNextAction,
  WorkflowExecutionSummary,
} from './workflowExecutions.api';
import { firstStepAlreadyRan } from './firstStepAlreadyRan';
import type { UnderwayActivityDetails } from './types';

// Map the execution's `next_action` enrichment onto the copy details the row
// renders (interview mode, survey audience, reviewer/HM recipients). Same for
// every candidate in a bulk session — they share the rule/action.
export const detailsFromNextAction = (
  nextAction: WorkflowExecutionNextAction | undefined
): UnderwayActivityDetails => ({
  scheduleMode: nextAction?.automation_mode,
  surveyType: nextAction?.survey_type,
  recipientKind: nextAction?.recipient_kind,
  recipients: nextAction?.recipients?.map((r) => ({
    name: r.name ?? '',
    avatarUrl: r.avatar_url ?? undefined,
  })),
});

// Enqueue input for a set of pending executions; null when there's nothing to
// show. `isBulk` false renders the single-candidate row (name + avatar) instead
// of the collapsed "N candidates" strip.
//
// Both immediate AND delayed first steps are enqueued: after the 15s undo the
// popup calls `/trigger`, and the BE (`ExecuteWorker#run_actions`) honors a
// per-action delay by re-scheduling it — so triggering a delayed step never
// runs it early. Only re-entry moves whose first step already ran are dropped
// (the BE would `already_created`-skip them, so they must not re-show).
export const buildEnqueueInputFromExecutions = (
  executions: WorkflowExecutionSummary[],
  options: { isBulk?: boolean } = {}
): EnqueueAutomationMoveInput | null => {
  const fresh = executions.filter((e) => !firstStepAlreadyRan(e.next_action));
  if (fresh.length === 0) return null;

  const first = fresh[0];
  const fallbackTarget = first.workflow_rule?.actions?.[0]?.target;
  const activityKind =
    first.next_action?.kind ?? fallbackTarget?.kind ?? 'email';
  const activityLabel =
    first.next_action?.label ?? fallbackTarget?.label ?? activityKind;

  const ruleSummary = first.workflow_rule?.summary;
  // All executions in one move share the same stage rule, so the first step's
  // delay is uniform — read it off the first to drive the "scheduled" copy.
  const delaySeconds = first.next_action?.delay_seconds ?? 0;

  return {
    activityKind,
    activityLabel,
    stageId: ruleSummary?.ats_job_stage?.id ?? '',
    stageName: ruleSummary?.ats_job_stage?.name ?? '',
    jobId: ruleSummary?.ats_job?.id ?? '',
    jobName: ruleSummary?.ats_job?.name ?? '',
    candidates: fresh.map((e) => ({
      id: String(e.subject?.id),
      name: e.subject?.name ?? `Candidate ${e.subject?.id}`,
      // BE sends the avatar under `subject.contact.image_url`.
      avatarUrl:
        e.subject?.avatar_url ?? e.subject?.contact?.image_url ?? undefined,
    })),
    isBulk: options.isBulk ?? true,
    details: detailsFromNextAction(first.next_action),
    executionIds: fresh.map((e) => e.id),
    delayed: delaySeconds > 0,
    delaySeconds: delaySeconds > 0 ? delaySeconds : undefined,
  };
};
